import { cameraErrorName, stopMediaStream } from '@/Pages/Selfie/helpers/cameraAccess'

export type CameraStreamTracker = {
  /**
   * Fecha TODO stream aberto enquanto o rastreador estava instalado — e também o
   * que ainda estava sendo aberto: ele chega depois, já de uma tentativa
   * descartada, e é fechado na chegada.
   */
  stopAll: () => void
  /** Nome do erro do último `getUserMedia` que falhou; null se o último deu certo. */
  lastErrorName: () => string | null
  /** Desde quando há um `getUserMedia` sem resposta (prompt aberto), ou null. */
  pendingSince: () => number | null
  uninstall: () => void
}

const NOOP_TRACKER: CameraStreamTracker = {
  stopAll: () => undefined,
  lastErrorName: () => null,
  pendingSince: () => null,
  uninstall: () => undefined,
}

/**
 * Registra cada stream que QUALQUER código da página abre pela câmera — o
 * detector do Amplify incluso — para que reabrir a câmera solte tudo, como um
 * refresh solta.
 *
 * Existe por um vazamento do Amplify (`checkVirtualCameraAndGetStream`): ele
 * abre um stream, falha numa checagem seguinte (15 fps, troca de `deviceId`) e
 * nunca para aquele primeiro stream. Sem a referência, nada fecha a câmera e
 * toda nova tentativa na página esbarra nela ocupada. De quebra, guarda o nome
 * do erro do navegador, que o Amplify perde no caminho (só repassa a mensagem)
 * — é ele que separa "permissão negada" de "câmera ocupada".
 *
 * Envolve `navigator.mediaDevices.getUserMedia` na própria instância, sem mexer
 * no protótipo, e `uninstall` devolve o que estava lá.
 */
export function installCameraStreamTracker(): CameraStreamTracker {
  const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (mediaDevices === undefined || typeof mediaDevices.getUserMedia !== 'function') {
    return NOOP_TRACKER
  }

  const streams = new Set<MediaStream>()
  const ownDescriptor = Object.getOwnPropertyDescriptor(mediaDevices, 'getUserMedia')
  const original = mediaDevices.getUserMedia
  let lastErrorName: string | null = null
  let epoch = 0
  let pending = 0
  let pendingSince: number | null = null

  const settle = () => {
    pending -= 1
    if (pending === 0) pendingSince = null
  }

  const tracked = (constraints?: MediaStreamConstraints): Promise<MediaStream> => {
    const requestEpoch = epoch
    pending += 1
    if (pendingSince === null) pendingSince = Date.now()
    return original.call(mediaDevices, constraints).then(
      (stream) => {
        settle()
        // Resposta de tentativa já descartada não fala pela atual.
        if (requestEpoch === epoch) {
          streams.add(stream)
          lastErrorName = null
        } else {
          stopMediaStream(stream)
        }
        return stream
      },
      (error: unknown) => {
        settle()
        if (requestEpoch === epoch) lastErrorName = cameraErrorName(error)
        throw error
      },
    )
  }

  try {
    Object.defineProperty(mediaDevices, 'getUserMedia', {
      configurable: true,
      writable: true,
      value: tracked,
    })
  } catch {
    // Navegador que não deixa sobrescrever: a câmera segue abrindo, só sem o
    // fechamento garantido na reabertura.
    return NOOP_TRACKER
  }

  return {
    stopAll: () => {
      epoch += 1
      streams.forEach(stopMediaStream)
      streams.clear()
    },
    lastErrorName: () => lastErrorName,
    pendingSince: () => pendingSince,
    uninstall: () => {
      // Alguém trocou depois de nós: não desfazer o que não é nosso.
      if (mediaDevices.getUserMedia !== tracked) return
      if (ownDescriptor !== undefined) {
        Object.defineProperty(mediaDevices, 'getUserMedia', ownDescriptor)
      } else {
        Reflect.deleteProperty(mediaDevices, 'getUserMedia')
      }
    },
  }
}
